/**
 * BookingWidgetSettings — Echo Settings screen for the public booking link
 * ─────────────────────────────────────────────────────────────────────────
 * Admin surface where the practice configures:
 *   - Enable/disable the public link
 *   - Practice name, tagline, welcome text
 *   - Logo URL, primary + accent color, background style
 *   - Which visit types appear publicly (subset of configured ones)
 *   - Copy the public URL + rotate it if compromised
 *
 * Live preview on the right so the practice sees exactly what patients see.
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import QRCode from 'react-qr-code';
import { Button } from '../../../ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../../ui/dialog';
import { Copy, RotateCcw, ExternalLink, Loader2, Sparkles, CheckCircle2, Lock, QrCode, Download, Printer, ClipboardList, Check, X, Clock, Calendar, Shield, MessageSquare, Link2 } from 'lucide-react';
import { cn } from '../../../../lib/utils';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('ariaone-token') || ''}` });

const DEFAULT_VISIT_TYPES = [
  { id: 'new-patient', name: 'New Patient', duration_min: 45, description: 'First visit — plan ~45 min including intake' },
  { id: 'follow-up', name: 'Follow-up', duration_min: 20, description: 'Routine follow-up with your provider' },
  { id: 'wellness', name: 'Wellness / Annual', duration_min: 30, description: 'Preventive exam and screenings' },
  { id: 'sick-visit', name: 'Sick Visit', duration_min: 20, description: 'Acute or urgent concern' },
];

const Section = ({ title, desc, children, testId }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3" data-testid={testId}>
    <div>
      <p className="text-[12px] font-semibold text-slate-800">{title}</p>
      {desc && <p className="text-[10px] text-slate-500 mt-0.5">{desc}</p>}
    </div>
    {children}
  </div>
);

const Toggle = ({ on, onToggle, testId }) => (
  <button
    onClick={onToggle}
    className={cn('relative inline-flex h-5 w-9 items-center rounded-full transition-colors shrink-0', on ? 'bg-[#111]' : 'bg-slate-200')}
    style={{ border: 'none', cursor: 'pointer' }}
    data-testid={testId}
    role="switch"
    aria-checked={on}
  >
    <span className="inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform" style={{ transform: on ? 'translateX(18px)' : 'translateX(2px)' }} />
  </button>
);

const ColorSwatch = null; // removed — theme is locked

/* ── Inline Preview (mirrors the locked B/W/G PublicBookingPage aesthetic) ── */
const Preview = ({ cfg }) => {
  const b = cfg.branding || {};
  const bg = '#F6F6F4';
  const surface = '#FFFFFF';
  const border = '#E8E5E0';
  return (
    <div
      className="rounded-xl overflow-hidden border border-slate-200 sticky top-4"
      data-testid="widget-preview"
      style={{ background: bg }}
    >
      <div className="px-3 py-2 bg-slate-100 border-b border-slate-200 flex items-center justify-between">
        <p className="text-[10px] font-semibold text-slate-500 uppercase">Live Preview</p>
        {cfg.slug && <span className="text-[9px] text-slate-400 font-mono">/book/{cfg.slug}</span>}
      </div>
      <div style={{ background: bg, padding: 16 }} className="min-h-[380px]">
        <div className="max-w-[280px] mx-auto text-center mb-3">
          {b.logo_url && <img src={b.logo_url} alt="" className="h-10 mx-auto mb-2 rounded-lg" />}
          <p className="text-[14px] font-semibold" style={{ color: '#1A1A1A', fontFamily: 'Lora, Georgia, serif' }}>{b.practice_name || 'Your Practice'}</p>
          {b.tagline && <p className="text-[10px] mt-0.5" style={{ color: '#5C5C5C' }}>{b.tagline}</p>}
        </div>
        <div className="rounded-2xl shadow-sm" style={{ background: surface, border: `1px solid ${border}` }}>
          <div className="px-3 py-2 border-b" style={{ borderColor: border }}>
            <p className="text-[9px]" style={{ color: '#9A9A9A' }}>Step 1 of 5 · Visit type</p>
          </div>
          <div className="p-3 space-y-2">
            <p className="text-[9px] font-semibold" style={{ color: '#1A1A1A' }}>What brings you in?</p>
            <div className="grid grid-cols-2 gap-1.5">
              {(cfg.visit_types || []).slice(0, 4).map((vt, i) => (
                <div key={vt.id} className="p-2 rounded-lg text-left"
                  style={{ border: `1.5px solid ${i === 0 ? '#111' : border}`, background: i === 0 ? '#F1F1F1' : 'transparent' }}>
                  <p className="text-[9px] font-semibold" style={{ color: '#1A1A1A' }}>{vt.name}</p>
                  <p className="text-[8px]" style={{ color: '#5C5C5C' }}>{vt.duration_min}m</p>
                </div>
              ))}
            </div>
            <div className="py-1.5 rounded-lg text-center text-[10px] font-semibold text-white mt-2" style={{ background: '#111' }}>
              Continue
            </div>
            <p className="text-[8px] text-center inline-flex items-center justify-center gap-1 w-full mt-1" style={{ color: '#9A9A9A' }}>
              Powered by Aria One
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const DEFAULT_POLICIES = {
  booking_mode: 'instant',
  hold_minutes: 15,
  confirmation_required: false,
  overbooking: 'off',
  overbooking_soft_pct: 10,
  slot_optimization: 'none',
  lead_time_hours: 2,
  booking_window_days: 30,
  cancel_window_hours: 24,
  approval_sla_hours: 4,
};

const BookingWidgetSettings = () => {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [rotateDialog, setRotateDialog] = useState(null); // { oldUrl } | null
  const [rotating, setRotating] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [tab, setTab] = useState('settings'); // 'settings' | 'review'
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [actingOn, setActingOn] = useState(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/api/echo/booking-widget`, { headers: authHeaders() });
      if (!data.visit_types || data.visit_types.length === 0) data.visit_types = DEFAULT_VISIT_TYPES;
      if (!data.branding) data.branding = { practice_name: 'Your Practice', primary_color: '#111111', accent_color: '#6b5ce7', background_style: 'light', show_provider_choice: true, show_insurance_info_note: true };
      if (!data.policies) data.policies = { ...DEFAULT_POLICIES };
      setCfg(data);
    } catch (e) {
      // Fall back to an empty local config so the admin can still configure the widget
      // (useful in demo/testing flows where the auth backend is not wired up).
      toast.info('Running in offline preview — save will attempt to persist when auth is available.');
      setCfg({
        org_id: null,
        slug: 'riverside-family-medicine',
        short_code: 'rv3k9x',
        enabled: true,
        branding: {
          practice_name: 'Riverside Family Medicine',
          tagline: 'Primary care for the whole family — since 1998',
          welcome_text: 'Thanks for choosing us. Tell us a bit about what you need and we will get back to you within one business day.',
          logo_url: '',
          primary_color: '#111111',
          accent_color: '#6b5ce7',
          background_style: 'light',
          show_provider_choice: true,
          show_insurance_info_note: true,
          footer_phone: '(555) 214-8800',
          footer_address: '100 Riverside Dr, Austin TX 78704',
        },
        visit_types: DEFAULT_VISIT_TYPES,
        policies: { ...DEFAULT_POLICIES },
        updated_at: null,
      });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const update = (path, val) => {
    setCfg((prev) => {
      const next = { ...prev };
      if (path.startsWith('branding.')) {
        next.branding = { ...next.branding, [path.slice('branding.'.length)]: val };
      } else if (path.startsWith('policies.')) {
        next.policies = { ...(next.policies || DEFAULT_POLICIES), [path.slice('policies.'.length)]: val };
      } else {
        next[path] = val;
      }
      return next;
    });
  };
  const toggleVisitType = (vt) => {
    setCfg((prev) => {
      const exists = prev.visit_types.find((x) => x.id === vt.id);
      return {
        ...prev,
        visit_types: exists
          ? prev.visit_types.filter((x) => x.id !== vt.id)
          : [...prev.visit_types, vt],
      };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled: cfg.enabled,
        branding: cfg.branding,
        visit_types: cfg.visit_types,
        policies: cfg.policies || DEFAULT_POLICIES,
      };
      const { data } = await axios.put(`${API}/api/echo/booking-widget`, payload, { headers: authHeaders() });
      setCfg(data);
      toast.success('Booking widget saved', { description: data.enabled ? `Live at /book/${data.slug}` : 'Widget is currently disabled.' });
    } catch (e) {
      toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  /* ── Review tab: pending approval requests + smart holds ── */
  const [holds, setHolds] = useState([]);
  const [recovering, setRecovering] = useState(null);
  const loadReviews = async () => {
    setReviewsLoading(true);
    try {
      const [pend, sh] = await Promise.all([
        axios.get(`${API}/api/echo/booking-widget/pending-reviews`, { headers: authHeaders() }),
        axios.get(`${API}/api/echo/booking-widget/smart-holds`, { headers: authHeaders() }),
      ]);
      setReviews(pend.data.pending || []);
      setHolds(sh.data.holds || []);
    } catch (e) {
      toast.error('Could not load pending reviews');
    } finally {
      setReviewsLoading(false);
    }
  };
  useEffect(() => { if (tab === 'review') loadReviews(); /* eslint-disable-next-line */ }, [tab]);

  const decide = async (request_id, action) => {
    setActingOn(request_id);
    try {
      const { data } = await axios.post(
        `${API}/api/echo/booking-widget/pending-reviews/${request_id}/${action}`,
        {},
        { headers: authHeaders() },
      );
      if (action === 'approve') {
        const sms = data?.echo_sms;
        if (sms?.message && sms?.status !== 'skipped_no_phone') {
          toast.success('Approved · Echo AI agent texted the patient', {
            description: `"${sms.message.slice(0, 140)}"${sms.message.length > 140 ? '…' : ''}`,
          });
        } else {
          toast.success('Booking approved and synced to Scheduler');
        }
      } else {
        toast.success('Booking rejected');
      }
      setReviews((prev) => prev.filter((r) => r.request_id !== request_id));
    } catch (e) {
      toast.error(`Could not ${action} booking`);
    } finally {
      setActingOn(null);
    }
  };

  const recoverHold = async (session_id) => {
    setRecovering(session_id);
    try {
      await axios.post(
        `${API}/api/echo/booking-widget/smart-holds/${session_id}/recover`,
        {}, { headers: authHeaders() },
      );
      toast.success('Recovery SMS sent', { description: 'Patient will get a finish-booking link.' });
      setHolds((prev) => prev.filter((h) => h.session_id !== session_id));
    } catch (e) {
      toast.error('Could not send recovery');
    } finally {
      setRecovering(null);
    }
  };

  const rotateSlug = () => {
    if (!cfg?.slug) return;
    // Open the confirm dialog with the old URL so staff can copy/share it
    // during the transition window before the new slug goes live.
    setRotateDialog({
      oldUrl: `${window.location.origin}/book/${cfg.slug}`,
      oldSlug: cfg.slug,
    });
  };

  const confirmRotate = async () => {
    setRotating(true);
    try {
      const { data } = await axios.post(`${API}/api/echo/booking-widget/rotate-slug`, {}, { headers: authHeaders() });
      setCfg((prev) => ({ ...prev, slug: data.slug }));
      toast.success('New slug generated', { description: `/book/${data.slug}` });
      setRotateDialog(null);
    } catch {
      toast.error('Could not rotate slug');
    } finally {
      setRotating(false);
    }
  };

  const copyOldUrl = () => {
    if (!rotateDialog?.oldUrl) return;
    navigator.clipboard.writeText(rotateDialog.oldUrl);
    toast.success('Old URL copied — share with patients in transition');
  };

  const publicUrl = useMemo(() => {
    if (!cfg?.slug) return null;
    // Prefer short code (mini URL) when available; falls back to full slug.
    if (cfg.short_code) return `${window.location.origin}/s/${cfg.short_code}`;
    return `${window.location.origin}/book/${cfg.slug}`;
  }, [cfg?.slug, cfg?.short_code]);

  const fullUrl = useMemo(() => {
    if (!cfg?.slug) return null;
    return `${window.location.origin}/book/${cfg.slug}`;
  }, [cfg?.slug]);

  const copyUrl = () => {
    if (!publicUrl) return;
    navigator.clipboard.writeText(publicUrl);
    toast.success('Link copied to clipboard');
  };

  if (loading) {
    return <div className="py-8 text-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400 mx-auto" /></div>;
  }
  if (!cfg) return null;

  return (
    <div className="space-y-4" data-testid="booking-widget-settings">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-semibold text-slate-800">Public Booking Widget</p>
          <p className="text-[11px] text-slate-500 mt-0.5">Give patients a clean, branded link to request appointments — HIPAA-aware by design.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 pr-3 border-r border-slate-200">
            <span className="text-[11px] font-medium text-slate-600">{cfg.enabled ? 'Live' : 'Disabled'}</span>
            <Toggle on={cfg.enabled} onToggle={() => update('enabled', !cfg.enabled)} testId="widget-enabled-toggle" />
          </div>
          {tab === 'settings' && (
            <Button size="sm" className="h-8 text-[11px] bg-[#111] hover:bg-[#222] gap-1" onClick={save} disabled={saving} data-testid="widget-save-btn">
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />} Save changes
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-slate-200" data-testid="widget-tabs">
        {[
          { id: 'settings', label: 'Settings', icon: Sparkles },
          { id: 'review', label: `Review${reviews.length ? ` (${reviews.length})` : ''}`, icon: ClipboardList },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={cn(
              'px-3 py-2 text-[12px] font-medium inline-flex items-center gap-1.5 border-b-2 -mb-px transition',
              tab === t.id ? 'border-[#111] text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-800',
            )}
            data-testid={`widget-tab-${t.id}`}
          >
            <t.icon className="h-3 w-3" /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'review' ? (
        <ReviewPanel
          reviews={reviews}
          holds={holds}
          loading={reviewsLoading}
          onRefresh={loadReviews}
          onDecide={decide}
          onRecoverHold={recoverHold}
          actingOn={actingOn}
          recovering={recovering}
        />
      ) : (
      <>
      {/* Public URL bar */}
      <div className="rounded-2xl border border-slate-200 bg-white p-3 flex items-center gap-3" data-testid="widget-url-bar">
        <Lock className="h-3.5 w-3.5 text-slate-400" />
        <div className="flex-1 min-w-0">
          <code className="text-[11px] text-slate-700 font-mono truncate block">
            {publicUrl || 'Save to generate your link'}
          </code>
          {publicUrl && cfg?.short_code && (
            <p className="text-[9px] text-slate-400 mt-0.5 flex items-center gap-1" data-testid="widget-url-short-hint">
              <Link2 className="h-2.5 w-2.5" /> Mini URL — resolves to {fullUrl}
            </p>
          )}
        </div>
        {publicUrl && (
          <>
            <Button size="sm" variant="outline" className="h-7 text-[10px] gap-1" onClick={copyUrl} data-testid="widget-copy-btn">
              <Copy className="h-3 w-3" /> Copy
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-[10px] gap-1" onClick={() => setQrOpen(true)} data-testid="widget-qr-btn">
              <QrCode className="h-3 w-3" /> QR
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-[10px] gap-1" onClick={() => window.open(publicUrl, '_blank')} data-testid="widget-open-btn">
              <ExternalLink className="h-3 w-3" /> Open
            </Button>
            <Button size="sm" variant="ghost" className="h-7 text-[10px] gap-1 text-red-600" onClick={rotateSlug} data-testid="widget-rotate-btn">
              <RotateCcw className="h-3 w-3" /> Rotate
            </Button>
          </>
        )}
      </div>

      {/* Two column: form + preview */}
      <div className="grid grid-cols-5 gap-4">
        <div className="col-span-3 space-y-3">
          <Section title="Practice identity" desc="How your practice introduces itself on the booking page." testId="widget-section-identity">
            <div>
              <label className="text-[10px] text-slate-500 font-semibold">Practice name</label>
              <input value={cfg.branding.practice_name || ''} onChange={(e) => update('branding.practice_name', e.target.value)}
                className="w-full mt-1 px-3 py-2 rounded-md border border-slate-200 text-[12px] outline-none bg-slate-50" data-testid="widget-practice-name" />
            </div>
            <div>
              <label className="text-[10px] text-slate-500 font-semibold">Tagline <span className="text-slate-400">(optional)</span></label>
              <input value={cfg.branding.tagline || ''} onChange={(e) => update('branding.tagline', e.target.value)}
                placeholder="Compassionate primary care, close to home."
                className="w-full mt-1 px-3 py-2 rounded-md border border-slate-200 text-[12px] outline-none bg-slate-50" data-testid="widget-tagline" />
            </div>
            <div>
              <label className="text-[10px] text-slate-500 font-semibold">Welcome text</label>
              <textarea rows={2} value={cfg.branding.welcome_text || ''} onChange={(e) => update('branding.welcome_text', e.target.value)}
                placeholder="Welcome! Please share a few details so we can confirm the best time for your visit."
                className="w-full mt-1 px-3 py-2 rounded-md border border-slate-200 text-[12px] outline-none bg-slate-50" data-testid="widget-welcome" />
            </div>
            <div>
              <label className="text-[10px] text-slate-500 font-semibold">Logo URL <span className="text-slate-400">(https only)</span></label>
              <input value={cfg.branding.logo_url || ''} onChange={(e) => update('branding.logo_url', e.target.value)}
                placeholder="https://yourpractice.com/logo.png"
                className="w-full mt-1 px-3 py-2 rounded-md border border-slate-200 text-[12px] outline-none bg-slate-50 font-mono" data-testid="widget-logo" />
            </div>
          </Section>

          <Section title="Visible visit types" desc="Only the types you enable here appear on the public page." testId="widget-section-types">
            <div className="space-y-1.5">
              {DEFAULT_VISIT_TYPES.map((vt) => {
                const on = !!cfg.visit_types.find((x) => x.id === vt.id);
                return (
                  <label key={vt.id} className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-slate-50 cursor-pointer" data-testid={`widget-vt-row-${vt.id}`}>
                    <input type="checkbox" checked={on} onChange={() => toggleVisitType(vt)} className="h-3.5 w-3.5 accent-[#111]" data-testid={`widget-vt-toggle-${vt.id}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-medium text-slate-800">{vt.name} <span className="text-slate-400 text-[9px]">· {vt.duration_min}m</span></p>
                      <p className="text-[9px] text-slate-500">{vt.description}</p>
                    </div>
                  </label>
                );
              })}
            </div>
          </Section>

          <Section title="Booking policies" desc="How bookings are accepted and synced to Echo Scheduler." testId="widget-section-policies">
            <PolicyEditor policies={cfg.policies || DEFAULT_POLICIES} onChange={(k, v) => update(`policies.${k}`, v)} />
          </Section>

          <Section title="Contact info" desc="Shown in the footer of your public booking page." testId="widget-section-footer">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] text-slate-500 font-semibold">Phone</label>
                <input value={cfg.branding.footer_phone || ''} onChange={(e) => update('branding.footer_phone', e.target.value)}
                  placeholder="(555) 123-4567"
                  className="w-full mt-1 px-3 py-2 rounded-md border border-slate-200 text-[12px] outline-none bg-slate-50" data-testid="widget-footer-phone" />
              </div>
              <div>
                <label className="text-[10px] text-slate-500 font-semibold">Email <span className="text-slate-400">(optional)</span></label>
                <input type="email" value={cfg.branding.footer_email || ''} onChange={(e) => update('branding.footer_email', e.target.value)}
                  placeholder="hello@yourpractice.com"
                  className="w-full mt-1 px-3 py-2 rounded-md border border-slate-200 text-[12px] outline-none bg-slate-50" data-testid="widget-footer-email" />
              </div>
            </div>
            <div>
              <label className="text-[10px] text-slate-500 font-semibold">Address</label>
              <input value={cfg.branding.footer_address || ''} onChange={(e) => update('branding.footer_address', e.target.value)}
                placeholder="100 Medical Pkwy, Suite 200, Austin TX"
                className="w-full mt-1 px-3 py-2 rounded-md border border-slate-200 text-[12px] outline-none bg-slate-50" data-testid="widget-footer-addr" />
            </div>
            <p className="text-[9px] text-slate-400 pt-1">
              Visual theme is locked to AriaOne&rsquo;s minimal black &amp; white aesthetic across every booking link. You only personalise your practice identity and contact details.
            </p>
          </Section>

          <div className="rounded-2xl bg-slate-50 border border-slate-200 p-3 text-[10px] text-slate-600 flex items-start gap-2">
            <Lock className="h-3 w-3 text-slate-500 mt-0.5 shrink-0" />
            <span>
              The public widget collects only minimum-necessary info (name, contact, preferred window). DOB, insurance, and medical history stay inside your portal. Requests are rate-limited per IP, and audit-logged with a salted hash.
            </span>
          </div>
        </div>

        <div className="col-span-2 space-y-3">
          <Preview cfg={cfg} />
          {publicUrl && (
            <div className="rounded-2xl border border-slate-200 bg-white p-3" data-testid="widget-qr-card">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="text-[11px] font-semibold text-slate-800 inline-flex items-center gap-1">
                    <QrCode className="h-3 w-3" /> Patient QR code
                  </p>
                  <p className="text-[9px] text-slate-500">Print at the front desk or embed in SMS reminders.</p>
                </div>
                <Button size="sm" variant="outline" className="h-7 text-[10px] gap-1" onClick={() => setQrOpen(true)} data-testid="widget-qr-expand-btn">
                  <ExternalLink className="h-3 w-3" /> Expand
                </Button>
              </div>
              <div className="flex items-center justify-center bg-white rounded-lg py-3" data-testid="widget-qr-mini">
                <QRCode value={publicUrl} size={128} bgColor="#ffffff" fgColor="#111111" />
              </div>
              <p className="text-[9px] text-slate-400 text-center mt-2 truncate font-mono">{publicUrl}</p>
            </div>
          )}
        </div>
      </div>
      </>
      )}

      <QrExpandDialog
        open={qrOpen}
        onOpenChange={setQrOpen}
        publicUrl={publicUrl}
        practiceName={cfg.branding?.practice_name}
      />
      <RotateConfirmDialog
        dialog={rotateDialog}
        onCancel={() => setRotateDialog(null)}
        onCopyOld={copyOldUrl}
        onConfirm={confirmRotate}
        rotating={rotating}
      />
    </div>
  );
};

/* ═══ QR code expand dialog with Download + Print ═══ */
const QrExpandDialog = ({ open, onOpenChange, publicUrl, practiceName }) => {
  const downloadSvg = () => {
    const svg = document.querySelector('[data-testid=widget-qr-large] svg');
    if (!svg) return;
    const src = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([src], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(practiceName || 'booking').toLowerCase().replace(/\W+/g, '-')}-qr.svg`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('QR code downloaded');
  };

  const printQr = () => {
    const win = window.open('', '_blank', 'width=600,height=800');
    if (!win || !publicUrl) return;
    const svg = document.querySelector('[data-testid=widget-qr-large] svg');
    const svgMarkup = svg ? new XMLSerializer().serializeToString(svg) : '';
    win.document.write(`<!DOCTYPE html><html><head><title>Book with ${practiceName || 'us'}</title>
      <style>
        body{font-family:-apple-system,system-ui,sans-serif;padding:40px;text-align:center;color:#111}
        .card{max-width:360px;margin:0 auto;border:1px solid #e2e8f0;border-radius:20px;padding:32px}
        h1{font-size:22px;margin:0 0 8px}
        p{font-size:13px;color:#64748b;margin:0 0 20px}
        .qr{display:inline-flex;padding:12px;background:#fff;border-radius:12px}
        code{display:block;margin-top:16px;font-size:11px;color:#475569;word-break:break-all}
        @media print{body{padding:0}}
      </style></head><body>
      <div class="card">
        <h1>Book with ${practiceName || 'us'}</h1>
        <p>Scan to request an appointment</p>
        <div class="qr">${svgMarkup}</div>
        <code>${publicUrl}</code>
      </div>
      <script>window.onload=()=>setTimeout(()=>window.print(),200);</script>
      </body></html>`);
    win.document.close();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="widget-qr-dialog">
        <DialogHeader>
          <DialogTitle className="text-[14px] font-semibold">Patient booking QR code</DialogTitle>
          <DialogDescription className="text-[11px]">
            Print this at the front desk, add it to patient handouts, or share via email/SMS.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center py-4" data-testid="widget-qr-large">
          {publicUrl && <QRCode value={publicUrl} size={220} bgColor="#ffffff" fgColor="#111111" />}
          <p className="text-[11px] text-slate-500 mt-3 text-center font-mono break-all px-2">{publicUrl}</p>
        </div>
        <DialogFooter className="flex-row gap-2 sm:justify-end">
          <Button variant="outline" size="sm" onClick={downloadSvg} className="h-8 text-[11px] gap-1" data-testid="widget-qr-download-btn">
            <Download className="h-3 w-3" /> Download SVG
          </Button>
          <Button variant="outline" size="sm" onClick={printQr} className="h-8 text-[11px] gap-1" data-testid="widget-qr-print-btn">
            <Printer className="h-3 w-3" /> Print
          </Button>
          <Button size="sm" className="h-8 text-[11px] bg-[#111] hover:bg-[#222]" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ═══ Rotate slug confirm — surfaces the old URL so staff can copy/share
   during the transition window before the new slug goes live. ═══ */
const RotateConfirmDialog = ({ dialog, onCancel, onCopyOld, onConfirm, rotating }) => {
  const open = !!dialog;
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent className="sm:max-w-md" data-testid="widget-rotate-dialog">
        <DialogHeader>
          <DialogTitle className="text-[14px] font-semibold text-red-700 inline-flex items-center gap-1.5">
            <RotateCcw className="h-3.5 w-3.5" /> Rotate public URL?
          </DialogTitle>
          <DialogDescription className="text-[11px]">
            The old link will stop working immediately. Copy it first if you need to
            message any patients in transition — we can't recover it once rotated.
          </DialogDescription>
        </DialogHeader>
        <div className="py-2 space-y-2">
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-2.5">
            <p className="text-[9px] uppercase text-slate-400 font-semibold mb-1">Current URL</p>
            <code className="block text-[11px] text-slate-700 font-mono break-all" data-testid="widget-rotate-old-url">
              {dialog?.oldUrl}
            </code>
            <div className="flex justify-end mt-2">
              <Button size="sm" variant="outline" className="h-7 text-[10px] gap-1" onClick={onCopyOld} data-testid="widget-rotate-copy-old-btn">
                <Copy className="h-3 w-3" /> Copy current URL
              </Button>
            </div>
          </div>
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-[11px] text-amber-800 flex items-start gap-2">
            <Lock className="h-3 w-3 mt-0.5 shrink-0" />
            <span>After rotating, patients visiting the old URL will see a 404. A new URL will be generated and appear in the URL bar.</span>
          </div>
        </div>
        <DialogFooter className="flex-row gap-2 sm:justify-end">
          <Button variant="outline" size="sm" onClick={onCancel} className="h-8 text-[11px]" disabled={rotating} data-testid="widget-rotate-cancel-btn">
            Cancel
          </Button>
          <Button size="sm" className="h-8 text-[11px] bg-red-600 hover:bg-red-700 gap-1" onClick={onConfirm} disabled={rotating} data-testid="widget-rotate-confirm-btn">
            {rotating ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />} Rotate now
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ═══ Policy editor ═══ */
const Radio = ({ value, current, onChange, label, hint, testId }) => (
  <button
    type="button"
    onClick={() => onChange(value)}
    className={cn(
      'flex-1 text-left px-3 py-2 rounded-lg border transition',
      current === value ? 'border-[#111] bg-slate-50' : 'border-slate-200 bg-white hover:border-slate-400',
    )}
    data-testid={testId}
  >
    <p className="text-[11px] font-semibold text-slate-800">{label}</p>
    {hint && <p className="text-[9px] text-slate-500 mt-0.5">{hint}</p>}
  </button>
);

const NumberInput = ({ value, onChange, suffix, min, max, testId }) => (
  <div className="inline-flex items-center gap-1">
    <input
      type="number"
      value={value ?? ''}
      min={min}
      max={max}
      onChange={(e) => {
        const n = parseInt(e.target.value, 10);
        if (Number.isNaN(n)) return;
        onChange(Math.max(min ?? 0, Math.min(max ?? 9999, n)));
      }}
      className="w-16 px-2 py-1 rounded-md border border-slate-200 text-[11px] outline-none bg-white text-center"
      data-testid={testId}
    />
    {suffix && <span className="text-[10px] text-slate-500">{suffix}</span>}
  </div>
);

const PolicyEditor = ({ policies, onChange }) => {
  const p = { ...DEFAULT_POLICIES, ...(policies || {}) };
  return (
    <div className="space-y-3" data-testid="policy-editor">
      {/* Booking mode */}
      <div>
        <p className="text-[10px] text-slate-500 font-semibold mb-1.5">Booking mode</p>
        <div className="flex gap-2">
          <Radio
            value="instant"
            current={p.booking_mode}
            onChange={(v) => onChange('booking_mode', v)}
            label="Instant book"
            hint="Slot confirmed immediately on submit."
            testId="policy-mode-instant"
          />
          <Radio
            value="approval"
            current={p.booking_mode}
            onChange={(v) => onChange('booking_mode', v)}
            label="Approval required"
            hint="Slot held, manager approves in Review tab."
            testId="policy-mode-approval"
          />
        </div>
        {p.booking_mode === 'approval' && (
          <>
            <div className="mt-2 grid grid-cols-2 gap-3 pl-1">
              <div className="inline-flex items-center justify-between text-[10px] text-slate-600">
                Hold for
                <NumberInput value={p.hold_minutes} onChange={(v) => onChange('hold_minutes', v)} suffix="min" min={1} max={120} testId="policy-hold-minutes" />
              </div>
              <div className="inline-flex items-center justify-between text-[10px] text-slate-600">
                Review SLA
                <NumberInput value={p.approval_sla_hours} onChange={(v) => onChange('approval_sla_hours', v)} suffix="hrs" min={1} max={48} testId="policy-approval-sla" />
              </div>
            </div>
            <div
              className="mt-2 flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-2"
              data-testid="policy-approval-echo-sms-note"
            >
              <MessageSquare className="h-3 w-3 text-slate-700 mt-0.5 shrink-0" />
              <p className="text-[10px] text-slate-700 leading-relaxed">
                <span className="font-semibold">Echo AI agent will text the patient once you approve.</span>
                {' '}The confirmation SMS includes the visit type, provider, and time — logged to Campaign sends.
              </p>
            </div>
          </>
        )}
      </div>

      {/* Confirmation */}
      <div className="flex items-center justify-between py-1">
        <div>
          <p className="text-[11px] font-semibold text-slate-800">Require patient confirmation</p>
          <p className="text-[9px] text-slate-500">Patient must reply YES to our text to hold the slot.</p>
        </div>
        <Toggle on={p.confirmation_required} onToggle={() => onChange('confirmation_required', !p.confirmation_required)} testId="policy-confirmation" />
      </div>

      {/* Overbooking */}
      <div>
        <p className="text-[10px] text-slate-500 font-semibold mb-1.5">Overbooking</p>
        <div className="flex gap-2">
          <Radio value="off"  current={p.overbooking} onChange={(v) => onChange('overbooking', v)} label="Off"  hint="No double-booking." testId="policy-overbook-off" />
          <Radio value="soft" current={p.overbooking} onChange={(v) => onChange('overbooking', v)} label="Soft" hint="Allow small % same-day double-books." testId="policy-overbook-soft" />
          <Radio value="hard" current={p.overbooking} onChange={(v) => onChange('overbooking', v)} label="Hard" hint="Unlimited same-slot bookings." testId="policy-overbook-hard" />
        </div>
        {p.overbooking === 'soft' && (
          <div className="mt-2 inline-flex items-center gap-2 text-[10px] text-slate-600">
            Cap
            <NumberInput value={p.overbooking_soft_pct} onChange={(v) => onChange('overbooking_soft_pct', v)} suffix="% per slot" min={0} max={50} testId="policy-overbook-pct" />
          </div>
        )}
      </div>

      {/* Slot optimization */}
      <div>
        <p className="text-[10px] text-slate-500 font-semibold mb-1.5">Slot optimization</p>
        <div className="flex gap-2">
          <Radio value="none"         current={p.slot_optimization} onChange={(v) => onChange('slot_optimization', v)} label="None"         hint="Show all openings."                      testId="policy-opt-none" />
          <Radio value="back_to_back" current={p.slot_optimization} onChange={(v) => onChange('slot_optimization', v)} label="Back-to-back" hint="Prefer contiguous slots — fewer gaps."   testId="policy-opt-btb" />
          <Radio value="spread_out"   current={p.slot_optimization} onChange={(v) => onChange('slot_optimization', v)} label="Spread out"   hint="Pad gaps — easier for complex visits."   testId="policy-opt-spread" />
        </div>
      </div>

      {/* Lead / window / cancel */}
      <div className="grid grid-cols-3 gap-3 pt-1">
        <div>
          <p className="text-[10px] text-slate-500 font-semibold mb-1 inline-flex items-center gap-1"><Clock className="h-3 w-3" /> Lead time</p>
          <NumberInput value={p.lead_time_hours} onChange={(v) => onChange('lead_time_hours', v)} suffix="hrs min" min={0} max={72} testId="policy-lead-time" />
        </div>
        <div>
          <p className="text-[10px] text-slate-500 font-semibold mb-1 inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> Booking window</p>
          <NumberInput value={p.booking_window_days} onChange={(v) => onChange('booking_window_days', v)} suffix="days ahead" min={1} max={180} testId="policy-booking-window" />
        </div>
        <div>
          <p className="text-[10px] text-slate-500 font-semibold mb-1 inline-flex items-center gap-1"><Shield className="h-3 w-3" /> Cancel window</p>
          <NumberInput value={p.cancel_window_hours} onChange={(v) => onChange('cancel_window_hours', v)} suffix="hrs" min={0} max={168} testId="policy-cancel-window" />
        </div>
      </div>
    </div>
  );
};


/* ═══ Review panel — pending approval-required bookings + smart holds ═══ */
const ReviewPanel = ({ reviews, holds = [], loading, onRefresh, onDecide, onRecoverHold, actingOn, recovering }) => {
  return (
    <div className="space-y-4" data-testid="booking-review-panel">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[12px] font-semibold text-slate-800 inline-flex items-center gap-1.5">
            <ClipboardList className="h-3.5 w-3.5" /> Pending approvals
          </p>
          <p className="text-[10px] text-slate-500 mt-0.5">Approve to sync with Echo Scheduler. Reject releases the hold.</p>
        </div>
        <Button size="sm" variant="outline" className="h-7 text-[10px] gap-1" onClick={onRefresh} data-testid="review-refresh-btn">
          <RotateCcw className="h-3 w-3" /> Refresh
        </Button>
      </div>

      {/* Smart Holds — abandoned mid-form sessions */}
      {holds.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3 space-y-2" data-testid="smart-holds-section">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[11px] font-semibold text-amber-900 inline-flex items-center gap-1.5">
                <Sparkles className="h-3 w-3" /> Smart Holds · {holds.length} drop-off{holds.length !== 1 ? 's' : ''}
              </p>
              <p className="text-[10px] text-amber-800/80 mt-0.5">
                Patient picked a slot but didn't finish. One tap sends a finish-booking SMS.
              </p>
            </div>
          </div>
          <div className="space-y-1.5">
            {holds.map((h) => (
              <div key={h.session_id} className="rounded-lg border border-amber-200 bg-white px-3 py-2 flex items-start justify-between gap-3" data-testid={`smart-hold-row-${h.session_id}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] font-medium text-slate-900 truncate">
                    {h.visit_type_name || h.visit_type_id} · {h.slot?.label}
                    {h.provider ? ` · ${h.provider}` : ''}
                  </p>
                  <p className="text-[10px] text-slate-500 mt-0.5">
                    Abandoned at contact step · started {h.created_at ? new Date(h.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'recently'}
                  </p>
                </div>
                <Button
                  size="sm"
                  className="h-7 text-[10px] gap-1 bg-amber-900 hover:bg-amber-800"
                  onClick={() => onRecoverHold(h.session_id)}
                  disabled={recovering === h.session_id}
                  data-testid={`smart-hold-recover-${h.session_id}`}
                >
                  {recovering === h.session_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />} Send finish-booking SMS
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-8 text-center"><Loader2 className="h-4 w-4 animate-spin text-slate-400 mx-auto" /></div>
      ) : reviews.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 py-10 text-center" data-testid="review-empty">
          <Sparkles className="h-4 w-4 text-slate-400 mx-auto mb-1" />
          <p className="text-[12px] font-medium text-slate-700">Inbox zero.</p>
          <p className="text-[10px] text-slate-500">Approval-required bookings will appear here.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {reviews.map((r) => (
            <div key={r.request_id} className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`review-row-${r.request_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-[12px] font-semibold text-slate-900 truncate">
                      {r.patient?.first_name} {r.patient?.last_name}
                    </p>
                    <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-800 font-medium">Hold</span>
                    {r.lens_captured && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-800 font-medium">Lens OK</span>}
                  </div>
                  <p className="text-[10px] text-slate-500 mt-0.5 truncate">
                    {r.visit_type_name || r.visit_type_id} · {r.slot?.label || r.preferred_window}
                    {r.provider_pref ? ` · ${r.provider_pref}` : ''}
                  </p>
                  <p className="text-[10px] text-slate-400 mt-1 inline-flex items-center gap-2">
                    <span>{r.patient?.phone}</span>
                    {r.patient?.email && <span>· {r.patient.email}</span>}
                    {r.hold_until && <span>· hold until {new Date(r.hold_until).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <Button
                    size="sm" variant="outline"
                    className="h-7 text-[10px] gap-1 text-red-600 border-red-200 hover:bg-red-50"
                    onClick={() => onDecide(r.request_id, 'reject')}
                    disabled={actingOn === r.request_id}
                    data-testid={`review-reject-${r.request_id}`}
                  >
                    <X className="h-3 w-3" /> Reject
                  </Button>
                  <Button
                    size="sm"
                    className="h-7 text-[10px] gap-1 bg-[#111] hover:bg-[#222]"
                    onClick={() => onDecide(r.request_id, 'approve')}
                    disabled={actingOn === r.request_id}
                    data-testid={`review-approve-${r.request_id}`}
                  >
                    {actingOn === r.request_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Approve
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


export default BookingWidgetSettings;
